import { useCallback, useEffect, useState, type FormEvent } from "react";
import { AREAS, AREA_LABELS, type Grant, type Level } from "@shared/permissions";
import { KIND_AREAS } from "@shared/sites";
import { hub, type HubSite, type HubUser, type Me } from "@/lib/hub";

// Owners manage the hub's people here: add someone by their Google email, make them an owner (everything, everywhere,
// and this screen) or a member (only what is chosen per website), pause or remove them. Every change is checked again
// by the hub's Worker (it keeps at least one owner and stops owners locking themselves out) and logged in Activity.
// While Google sign-in is in "Testing", a new person must also be added as a test user in Google Cloud.

const INPUT = "w-full rounded-lg border border-midnight/20 bg-white px-3 py-2.5 text-midnight focus:border-teal focus:outline-none";
const BTN = "inline-flex min-h-11 items-center justify-center rounded-lg px-4 text-sm font-bold transition-colors disabled:opacity-60";
const LEVELS: { value: Level; label: string }[] = [
  { value: "none", label: "No access" },
  { value: "view", label: "View" },
  { value: "edit", label: "Edit" },
];

const seen = (iso: string | null) => (iso ? `last signed in ${new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : "hasn't signed in yet");

export function Users({ sites, me }: { sites: HubSite[]; me: Me }) {
  const [users, setUsers] = useState<HubUser[] | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(() => hub.users().then(setUsers, (e: unknown) => setNotice({ ok: false, text: e instanceof Error ? e.message : "Couldn't load the users." })), []);
  useEffect(() => {
    void load();
  }, [load]);

  const act = async (work: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setNotice(null);
    try {
      await work();
      setNotice({ ok: true, text: done });
      await load();
    } catch (e) {
      setNotice({ ok: false, text: e instanceof Error ? e.message : "That didn't work." });
    } finally {
      setBusy(false);
    }
  };

  const add = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const email = String(data.get("email") ?? "").trim().toLowerCase();
    void act(async () => {
      await hub.addUser({ email, name: String(data.get("name") ?? ""), role: data.get("role") === "owner" ? "owner" : "member" });
      form.reset();
      setOpen(email);
    }, `Added ${email}. Choose what they can do below, and add them as a test user in Google Cloud so they can sign in.`);
  };

  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight">Users</h1>
      <p className="mt-2 mb-6 text-midnight/70">Who can sign in to the hub, and what they can do on each website. Owners can do everything, including this page.</p>

      <form onSubmit={add} className="mb-8 grid gap-3 rounded-2xl border border-midnight/10 bg-sand p-4 sm:grid-cols-[2fr_1.4fr_1fr_auto] sm:items-end sm:p-5">
        <label className="flex flex-col gap-1 text-sm font-bold">
          Google email
          <input name="email" type="email" required inputMode="email" autoComplete="off" placeholder="name@gmail.com" className={INPUT} />
        </label>
        <label className="flex flex-col gap-1 text-sm font-bold">
          Name (optional)
          <input name="name" maxLength={120} className={INPUT} />
        </label>
        <label className="flex flex-col gap-1 text-sm font-bold">
          Role
          <select name="role" defaultValue="member" className={INPUT}>
            <option value="member">Member</option>
            <option value="owner">Owner</option>
          </select>
        </label>
        <button type="submit" disabled={busy} className={`${BTN} bg-midnight text-offwhite hover:bg-teal`}>
          + Add
        </button>
      </form>

      {notice && (
        <p role="status" className={`mb-4 rounded-xl border p-3 text-sm ${notice.ok ? "border-teal/40 bg-teal/5" : "border-red-700/30 bg-red-50 text-red-900"}`}>
          {notice.text}
        </p>
      )}
      {!users && <p className="text-midnight/70">Loading…</p>}
      <ul className="flex flex-col gap-3">
        {users?.map((u) => (
          <li key={u.email} className={`rounded-2xl border p-4 sm:p-5 ${u.disabled ? "border-dashed border-midnight/25 bg-offwhite" : "border-midnight/10 bg-sand"}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <div className="min-w-0">
                <h2 className="text-lg font-bold break-all">
                  {u.name || u.email}
                  {u.email === me.email && <span className="ms-2 text-sm font-normal text-midnight/60">(you)</span>}
                </h2>
                {u.name && <p className="text-sm break-all text-midnight/70">{u.email}</p>}
              </div>
              <span className="font-mono text-xs tracking-[0.1em] uppercase">
                {u.role}
                {u.disabled && " · paused"}
              </span>
            </div>
            <p className="mt-1 text-xs text-midnight/60">{seen(u.lastSeen)}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {u.role === "member" && (
                <button type="button" onClick={() => setOpen(open === u.email ? null : u.email)} aria-expanded={open === u.email} className={`${BTN} border border-midnight/20 hover:border-teal hover:text-teal`}>
                  {open === u.email ? "Close permissions" : "Permissions…"}
                </button>
              )}
              {u.email !== me.email && (
                <>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => act(() => hub.updateUser(u.email, { role: u.role === "owner" ? "member" : "owner" }), u.role === "owner" ? "Now a member." : "Now an owner.")}
                    className={`${BTN} border border-midnight/20 hover:border-teal hover:text-teal`}
                  >
                    {u.role === "owner" ? "Make member" : "Make owner"}
                  </button>
                  <button type="button" disabled={busy} onClick={() => act(() => hub.updateUser(u.email, { disabled: !u.disabled }), u.disabled ? "Can sign in again." : "Paused: they can't sign in.")} className={`${BTN} border border-midnight/20 hover:border-teal hover:text-teal`}>
                    {u.disabled ? "Allow again" : "Pause"}
                  </button>
                  <RemoveButton disabled={busy} onConfirm={() => act(() => hub.removeUser(u.email), `Removed ${u.email}.`)} />
                </>
              )}
            </div>
            {open === u.email && u.role === "member" && <Permissions user={u} sites={sites} busy={busy} save={(siteId, grant) => act(() => hub.setGrant(u.email, siteId, grant), "Permissions saved.")} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RemoveButton({ onConfirm, disabled }: { onConfirm: () => void; disabled: boolean }) {
  const [asking, setAsking] = useState(false);
  if (!asking)
    return (
      <button type="button" disabled={disabled} onClick={() => setAsking(true)} className={`${BTN} border border-red-700/30 text-red-800 hover:bg-red-700 hover:text-white`}>
        Remove
      </button>
    );
  return (
    <span className="flex flex-wrap items-center gap-2 text-sm">
      Remove this person?
      <button type="button" onClick={() => (setAsking(false), onConfirm())} className={`${BTN} bg-red-700 text-white hover:bg-red-800`}>
        Yes, remove
      </button>
      <button type="button" onClick={() => setAsking(false)} className={`${BTN} border border-midnight/20`}>
        Cancel
      </button>
    </span>
  );
}

/** A member's permissions: one table per website, a level per area. */
function Permissions({ user, sites, busy, save }: { user: HubUser; sites: HubSite[]; busy: boolean; save: (siteId: string, grant: Grant) => void }) {
  const [draft, setDraft] = useState<Record<string, Grant>>(() => Object.fromEntries(sites.map((s) => [s.id, { ...(user.grants[s.id] ?? {}) }])));
  return (
    <div className="mt-4 flex flex-col gap-4">
      {sites.map((site) => (
        <fieldset key={site.id} className="rounded-xl border border-midnight/10 bg-offwhite p-3 sm:p-4">
          <legend className="px-1 font-bold">{site.name}</legend>
          <div className="grid gap-2">
            {AREAS.filter((area) => KIND_AREAS[site.kind][area]).map((area) => (
              <label key={area} className="grid items-center gap-x-3 gap-y-1 sm:grid-cols-[1fr_10rem]">
                <span>
                  <span className="font-bold">{AREA_LABELS[area].name}</span>
                  <span className="block text-xs text-midnight/60">{KIND_AREAS[site.kind][area]}</span>
                </span>
                <select
                  value={draft[site.id]?.[area] ?? "none"}
                  onChange={(e) => setDraft((d) => ({ ...d, [site.id]: { ...d[site.id], [area]: e.target.value as Level } }))}
                  className={INPUT}
                  aria-label={`${AREA_LABELS[area].name} on ${site.name}`}
                >
                  {LEVELS.filter((l) => area !== "stats" || l.value !== "edit").map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          <button type="button" disabled={busy} onClick={() => save(site.id, draft[site.id] ?? {})} className={`${BTN} mt-3 bg-midnight text-offwhite hover:bg-teal`}>
            Save for {site.name}
          </button>
        </fieldset>
      ))}
    </div>
  );
}
